// Every backend route in one place (relative to VITE_API_PATH): each entry is a
// RouteDef carrying its url + HTTP method + which token to attach, so endpoints
// just spread the descriptor (`{ ...ROUTES.auth.login, data }`). Static routes
// are RouteDef objects; parameterized routes are functions returning a RouteDef.
// Query-string params do NOT belong here — pass them via the base query's
// `params` field.

import type { RouteDef } from './types';

export const ROUTES = {
  handShake: {
    startup: { url: 'startup', method: 'POST', tokenType: 'staticToken' } satisfies RouteDef,
  },
  auth: {
    login: { url: 'users/login', method: 'POST', tokenType: 'staticToken' } satisfies RouteDef,
    register: {
      url: 'users/register',
      method: 'POST',
      tokenType: 'staticToken',
    } satisfies RouteDef,
    // Two sessions, one refresh endpoint each — the path segment names which:
    // `/company` renews the company session (authToken, authorized by
    // refreshToken), `/user` renews the login session (loginToken, authorized by
    // loginRefreshToken). Neither can renew the other's.
    refreshCompany: {
      url: 'users/refresh-token/company',
      method: 'GET',
      tokenType: 'refreshToken',
    } satisfies RouteDef,
    refreshUser: {
      url: 'users/refresh-token/user',
      method: 'GET',
      tokenType: 'loginRefreshToken',
    } satisfies RouteDef,
    // Send / resend a one-time code for register or password reset.
    sendOtp: {
      url: 'users/send-otp',
      method: 'POST',
      tokenType: 'staticToken',
    } satisfies RouteDef,
    verifyOtp: {
      url: 'users/verify-otp',
      method: 'POST',
      tokenType: 'staticToken',
    } satisfies RouteDef,
    resetPassword: {
      url: 'users/reset-password',
      method: 'POST',
      // Authorized by the OTP-verification token (stored in auth.validationKey),
      // not the static token.
      tokenType: 'validationKey',
    } satisfies RouteDef,
  },
  // The signed-in user's own account — identity plus a `companies[]` array of
  // their membership in each company. Company-scoped token even though the path
  // is user-scoped (confirmed against the live endpoint).
  users: {
    ownProfile: {
      url: 'users/own-profile',
      method: 'GET',
      tokenType: 'authToken',
    } satisfies RouteDef,
    // Name / email / app-language only. `email` always rides along, null when
    // cleared; `appLanguage` is a stored preference, not the web UI's language.
    updateOwnProfile: {
      url: 'users/update-own-profile',
      method: 'POST',
      tokenType: 'authToken',
    } satisfies RouteDef,
    // Mails a 6-digit code to the user's saved address. No body — the session
    // token identifies the user, and the address is whatever the server holds.
    sendEmailVerificationOtp: {
      url: 'users/send-email-verification-otp',
      method: 'POST',
      tokenType: 'authToken',
    } satisfies RouteDef,
    // Confirms an emailed verification code. staticToken, not authToken: the
    // link is opened from the mail client, usually in a tab with no session.
    verifyEmail: {
      url: 'users/verify-email',
      method: 'POST',
      tokenType: 'staticToken',
    } satisfies RouteDef,
    // Signed-in password change — distinct from auth.resetPassword, which is
    // the OTP-keyed forgot-password flow authorized by a validationKey.
    updatePassword: {
      url: 'users/update-password',
      method: 'POST',
      tokenType: 'authToken',
    } satisfies RouteDef,
    // The signed-in user's accessible structure nodes with their geofence
    // (`coordinates` "lat,lng" | null, `radius` metres | null) — the areas the
    // live-tracking map draws.
    ownNodes: {
      url: 'users/own-nodes',
      method: 'GET',
      tokenType: 'authToken',
    } satisfies RouteDef,
  },
  companies: {
    list: {
      url: 'users/all-companies',
      method: 'GET',
      tokenType: 'loginToken',
    } satisfies RouteDef,
    selectCompany: {
      url: 'users/select-company',
      method: 'POST',
      tokenType: 'loginToken',
    } satisfies RouteDef,
    // Pending company invitations for the logged-in user (pre-company screen).
    invites: {
      url: 'users/company-invites',
      method: 'GET',
      tokenType: 'loginToken',
    } satisfies RouteDef,
    acceptInvite: {
      url: 'users/accept-invite',
      method: 'POST',
      tokenType: 'loginToken',
    } satisfies RouteDef,
    create: {
      url: 'companies/create-company',
      method: 'POST',
      tokenType: 'loginToken',
    } satisfies RouteDef,
    byId: (id: string): RouteDef => ({
      url: `companies/${id}`,
      method: 'GET',
      tokenType: 'authToken',
    }),
    // Owner of the current company ({ companyUserId }) — company-scoped, so it
    // runs right after select-company stores the new authToken.
    ownerDetails: {
      url: 'companies/owner-details',
      method: 'GET',
      tokenType: 'authToken',
    } satisfies RouteDef,
    // Invite-by-link settings — GET current config + POST to save.
    inviteLinkDetails: {
      url: 'companies/invite-link-details',
      method: 'GET',
      tokenType: 'authToken',
    } satisfies RouteDef,
    saveInviteLinkDetails: {
      url: 'companies/invite-link-details',
      method: 'POST',
      tokenType: 'authToken',
    } satisfies RouteDef,
    // Texts the current invite link to the caller's own mobile. Bodyless: the
    // server resolves both the destination number and the invite code from the
    // session, so there is nothing for the client to pass.
    sendLinkToMobile: {
      url: 'companies/send-link-to-mobile',
      method: 'POST',
      tokenType: 'authToken',
    } satisfies RouteDef,
    // ---- Company settings (/settings → General tab) ----
    // One read feeds both cards: the Formats fields arrive nested under
    // `formatSettings`. No id in the path — the server resolves the company
    // from the session, as ownerDetails does.
    companyDetails: {
      url: 'companies/company-details',
      method: 'GET',
      tokenType: 'authToken',
    } satisfies RouteDef,
    // Each card saves its own section, and both POST the FULL object rather
    // than a diff — untouched fields still have to ride along.
    updateCompanyDetails: {
      url: 'companies/company-details',
      method: 'POST',
      tokenType: 'authToken',
    } satisfies RouteDef,
    updateCompanyFormats: {
      url: 'companies/company-settings-format',
      method: 'POST',
      tokenType: 'authToken',
    } satisfies RouteDef,
    // Additional settings tab. Each card saves its own section, and each takes
    // the FULL section object — a colour-only change still echoes `media` back.
    updateBrandDetails: {
      url: 'companies/brand-details',
      method: 'POST',
      tokenType: 'authToken',
    } satisfies RouteDef,
    updateWelcomeScreen: {
      url: 'companies/welcome-screen',
      method: 'POST',
      tokenType: 'authToken',
    } satisfies RouteDef,
    updateSupportContact: {
      url: 'companies/support-contact',
      method: 'POST',
      tokenType: 'authToken',
    } satisfies RouteDef,
  },
  companyUsers: {
    selection: {
      url: 'company-users/users/selection',
      method: 'GET',
      tokenType: 'authToken',
    } satisfies RouteDef,
    list: {
      url: 'company-users/users',
      method: 'POST',
      tokenType: 'authToken',
    } satisfies RouteDef,
    // Add person — confirmed route (payload shape still to-confirm).
    create: {
      url: 'company-users/invite-users',
      method: 'POST',
      tokenType: 'authToken',
    } satisfies RouteDef,
    // CSV import — same per-user shape as invite-users, batched in `users`.
    importUsers: {
      url: 'company-users/import-users',
      method: 'POST',
      tokenType: 'authToken',
    } satisfies RouteDef,
    // Person profile — full detail (identity, structure, roles + computed access).
    detail: (id: string): RouteDef => ({
      url: `company-users/users/detail/${id}`,
      method: 'GET',
      tokenType: 'authToken',
    }),
    // Edit person — PATCH the full profile (identity + placements/roles/tags/groups).
    update: (id: string): RouteDef => ({
      url: `company-users/users/${id}`,
      method: 'PATCH',
      tokenType: 'authToken',
    }),
    // Bulk actions on selected company users (assign/remove role, add/remove
    // structure, archive, restore) — POST company-users/bulk-action.
    bulkAction: {
      url: 'company-users/bulk-action',
      method: 'POST',
      tokenType: 'authToken',
    } satisfies RouteDef,
    // Pending-tab lifecycle — ACCEPTED/REJECTED decide a pending request,
    // DELETED withdraws an admin invite. Bulk-capable, so one row and a whole
    // selection are the same call.
    updateInviteStatus: {
      url: 'company-users/update-invite-status',
      method: 'POST',
      tokenType: 'authToken',
    } satisfies RouteDef,
    // Permanently delete archived company users. Bulk-capable like the above.
    // Only reachable from the Archived tab — deleting a person is archive-first
    // by product decision, so the People tab offers Archive and never Delete.
    bulkDelete: {
      url: 'company-users/bulk-delete',
      method: 'POST',
      tokenType: 'authToken',
    } satisfies RouteDef,
  },
  guests: {
    // Guest list — same search/pagination semantics as the people list, but the
    // body is only { page, limit, search? } (no status, no filter id arrays).
    all: {
      url: 'guests/all',
      method: 'POST',
      tokenType: 'authToken',
    } satisfies RouteDef,
    // Add guest — flat identity body { firstName, lastName, email?, countryCode?, mobileNumber? }.
    create: {
      url: 'guests',
      method: 'POST',
      tokenType: 'authToken',
    } satisfies RouteDef,
    detail: (id: string): RouteDef => ({
      url: `guests/${id}/details`,
      method: 'GET',
      tokenType: 'authToken',
    }),
    update: (id: string): RouteDef => ({
      url: `guests/${id}`,
      method: 'PATCH',
      tokenType: 'authToken',
    }),
    remove: (id: string): RouteDef => ({
      url: `guests/${id}`,
      method: 'DELETE',
      tokenType: 'authToken',
    }),
    // Bulk delete — body { guestIds: string[] }.
    deleteMultiple: {
      url: 'guests/delete-multiple',
      method: 'POST',
      tokenType: 'authToken',
    } satisfies RouteDef,
  },
  media: {
    // Reserves a presigned upload URL for a company logo (pre-company session).
    initialLogo: {
      url: 'media/initial-logo',
      method: 'POST',
      tokenType: 'loginToken',
    } satisfies RouteDef,
    // Reserves a presigned upload URL for form media (instructions reference
    // file, image-field reference image).
    formMedia: {
      url: 'media/form-media',
      method: 'POST',
      tokenType: 'authToken',
    } satisfies RouteDef,
    // Resolves an uploaded form media id to a displayable URL.
    getFormMedia: (id: string): RouteDef => ({
      url: `media/response/get-form-media/${id}`,
      method: 'GET',
      tokenType: 'authToken',
    }),
    // Reserves a presigned upload URL for a PUBLIC file — no owning entity, so
    // unlike formMedia it takes only a name. Used by the data source CSV import,
    // whose commit payload references the uploaded file by URL rather than by a
    // mediaId.
    uploadPublicFile: {
      url: 'media/upload-public-file',
      method: 'POST',
      tokenType: 'authToken',
    } satisfies RouteDef,
    // Batch-resolves attendance punch photos ({mediaType, imageName} items) to
    // download URLs, keyed back by imageName — one call per table page.
    downloadClockInMedia: {
      url: 'media/download-clock-in-media',
      method: 'POST',
      tokenType: 'authToken',
    } satisfies RouteDef,
    // Reserves a presigned upload URL for the company's brand logo. The
    // company-scoped counterpart to initialLogo, which is loginToken-only
    // (onboarding) — and it needs no customFolderId.
    brandDetailsMedia: {
      url: 'media/brand-details-media',
      method: 'POST',
      tokenType: 'authToken',
    } satisfies RouteDef,
    // Reserves a presigned upload URL for the mobile welcome screen's image.
    // Separate from brandDetailsMedia so the two images stay distinct
    // server-side; both resolve through companyProfileMedia below.
    welcomeScreenMedia: {
      url: 'media/welcome-screen-media',
      method: 'POST',
      tokenType: 'authToken',
    } satisfies RouteDef,
    // Resolves a company-profile mediaId to a displayable URL. The company
    // counterpart to getFormMedia, which is form-scoped. Serves both reserves.
    companyProfileMedia: (id: string): RouteDef => ({
      url: `media/company-profile-media/${id}`,
      method: 'GET',
      tokenType: 'authToken',
    }),
  },
  groups: {
    selection: {
      url: 'groups/selection',
      method: 'GET',
      tokenType: 'authToken',
    } satisfies RouteDef,
    create: { url: 'groups', method: 'POST', tokenType: 'authToken' } satisfies RouteDef,
    update: (id: string): RouteDef => ({
      url: `groups/${id}`,
      method: 'PATCH',
      tokenType: 'authToken',
    }),
    // Section reassignment only — body { groupSection }. Its own endpoint, kept
    // apart from the group PATCH above, which carries name / users / join link.
    move: (id: string): RouteDef => ({
      url: `groups/${id}/move`,
      method: 'PATCH',
      tokenType: 'authToken',
    }),
    remove: (id: string): RouteDef => ({
      url: `groups/${id}`,
      method: 'DELETE',
      tokenType: 'authToken',
    }),
  },
  groupSections: {
    list: { url: 'group-sections', method: 'GET', tokenType: 'authToken' } satisfies RouteDef,
    create: { url: 'group-sections', method: 'POST', tokenType: 'authToken' } satisfies RouteDef,
    // Rename: body { sectionName }. Delete: no body, and only ever called for an
    // empty section (the Groups tab blocks it otherwise — the server has no
    // reparent story for the groups inside).
    update: (id: string): RouteDef => ({
      url: `group-sections/${id}`,
      method: 'PATCH',
      tokenType: 'authToken',
    }),
    remove: (id: string): RouteDef => ({
      url: `group-sections/${id}`,
      method: 'DELETE',
      tokenType: 'authToken',
    }),
  },
  projects: {
    list: { url: 'projects/all', method: 'POST', tokenType: 'authToken' } satisfies RouteDef,
    // Lightweight project options for pickers ({id, title, projectType}).
    selection: {
      url: 'projects/selection',
      method: 'GET',
      tokenType: 'authToken',
    } satisfies RouteDef,
    create: { url: 'projects/create', method: 'POST', tokenType: 'authToken' } satisfies RouteDef,
    detail: (id: string): RouteDef => ({
      url: `projects/detail/${id}`,
      method: 'GET',
      tokenType: 'authToken',
    }),
    update: (id: string): RouteDef => ({
      url: `projects/update/${id}`,
      method: 'PATCH',
      tokenType: 'authToken',
    }),
    // One project's operational active/inactive flag — the `state` column, NOT
    // the lifecycle `updateStatus` below.
    updateState: (id: string): RouteDef => ({
      url: `projects/update/${id}/state`,
      method: 'PATCH',
      tokenType: 'authToken',
    }),
    // Bulk lifecycle move — archive/restore the given ids in one call.
    updateStatus: {
      url: 'projects/update/status',
      method: 'POST',
      tokenType: 'authToken',
    } satisfies RouteDef,
    remove: (id: string): RouteDef => ({
      url: `projects/delete/${id}`,
      method: 'DELETE',
      tokenType: 'authToken',
    }),
  },
  roles: {
    list: { url: 'roles', method: 'GET', tokenType: 'authToken' } satisfies RouteDef,
    // Lightweight role options for pickers ({id, title}).
    selection: {
      url: 'roles/selection',
      method: 'GET',
      tokenType: 'authToken',
    } satisfies RouteDef,
    // Per-role permission definitions (to recompute effective access client-side).
    selectionDetailed: {
      url: 'roles/selection/detailed',
      method: 'GET',
      tokenType: 'authToken',
    } satisfies RouteDef,
    byId: (id: string): RouteDef => ({ url: `roles/${id}`, method: 'GET', tokenType: 'authToken' }),
    create: { url: 'roles', method: 'POST', tokenType: 'authToken' } satisfies RouteDef,
    update: (id: string): RouteDef => ({
      url: `roles/${id}`,
      method: 'PATCH',
      tokenType: 'authToken',
    }),
    remove: (id: string): RouteDef => ({
      url: `roles/${id}`,
      method: 'DELETE',
      tokenType: 'authToken',
    }),
  },
  modules: {
    list: { url: 'modules', method: 'GET', tokenType: 'authToken' } satisfies RouteDef,
  },
  structure: {
    tree: { url: 'company-structure', method: 'GET', tokenType: 'authToken' } satisfies RouteDef,
    selection: {
      url: 'company-structure/selection',
      method: 'GET',
      tokenType: 'authToken',
    } satisfies RouteDef,
    addNode: {
      url: 'company-structure/add-node',
      method: 'POST',
      tokenType: 'authToken',
    } satisfies RouteDef,
    nodeDetails: (id: string): RouteDef => ({
      url: `company-structure/node-details/${id}`,
      method: 'GET',
      tokenType: 'authToken',
    }),
    updateNode: (id: string): RouteDef => ({
      url: `company-structure/update-node/${id}`,
      method: 'PATCH',
      tokenType: 'authToken',
    }),
    updateNodesStatus: {
      url: 'company-structure/update-nodes-status',
      method: 'PATCH',
      tokenType: 'authToken',
    } satisfies RouteDef,
    selectionByLevel: {
      url: 'company-structure/selection-by-level',
      method: 'POST',
      tokenType: 'authToken',
    } satisfies RouteDef,
  },
  levels: {
    list: { url: 'company-levels', method: 'GET', tokenType: 'authToken' } satisfies RouteDef,
    selection: {
      url: 'company-levels/selection',
      method: 'GET',
      tokenType: 'authToken',
    } satisfies RouteDef,
    update: {
      url: 'company-levels/update-levels',
      method: 'POST',
      tokenType: 'authToken',
    } satisfies RouteDef,
  },
  tags: {
    list: {
      url: 'tags/company-structure-tags',
      method: 'GET',
      tokenType: 'authToken',
    } satisfies RouteDef,
    create: {
      url: 'tags/create-company-structure-tag',
      method: 'POST',
      tokenType: 'authToken',
    } satisfies RouteDef,
    update: (id: string): RouteDef => ({
      url: `tags/update-company-structure-tag/${id}`,
      method: 'PATCH',
      tokenType: 'authToken',
    }),
    remove: (id: string): RouteDef => ({
      url: `tags/delete-company-structure-tag/${id}`,
      method: 'DELETE',
      tokenType: 'authToken',
    }),
    projectList: {
      url: 'tags/project-tags',
      method: 'GET',
      tokenType: 'authToken',
    } satisfies RouteDef,
    createProject: {
      url: 'tags/create-project-tag',
      method: 'POST',
      tokenType: 'authToken',
    } satisfies RouteDef,
    updateProject: (id: string): RouteDef => ({
      url: `tags/update-project-tag/${id}`,
      method: 'PATCH',
      tokenType: 'authToken',
    }),
    removeProject: (id: string): RouteDef => ({
      url: `tags/delete-project-tag/${id}`,
      method: 'DELETE',
      tokenType: 'authToken',
    }),
    peopleList: {
      url: 'tags/people-tags',
      method: 'GET',
      tokenType: 'authToken',
    } satisfies RouteDef,
    createPeople: {
      url: 'tags/create-people-tag',
      method: 'POST',
      tokenType: 'authToken',
    } satisfies RouteDef,
    inspectionList: {
      url: 'tags/inspection-tags',
      method: 'GET',
      tokenType: 'authToken',
    } satisfies RouteDef,
    createInspection: {
      url: 'tags/create-inspection-tag',
      method: 'POST',
      tokenType: 'authToken',
    } satisfies RouteDef,
    updateInspection: (id: string): RouteDef => ({
      url: `tags/update-inspection-tag/${id}`,
      method: 'PATCH',
      tokenType: 'authToken',
    }),
    removeInspection: (id: string): RouteDef => ({
      url: `tags/delete-inspection-tag/${id}`,
      method: 'DELETE',
      tokenType: 'authToken',
    }),
    updatePeople: (id: string): RouteDef => ({
      url: `tags/update-people-tag/${id}`,
      method: 'PATCH',
      tokenType: 'authToken',
    }),
    removePeople: (id: string): RouteDef => ({
      url: `tags/delete-people-tag/${id}`,
      method: 'DELETE',
      tokenType: 'authToken',
    }),
    workflowResponseList: {
      url: 'tags/workflow-response-tags',
      method: 'GET',
      tokenType: 'authToken',
    } satisfies RouteDef,
    createWorkflowResponse: {
      url: 'tags/create-workflow-response-tag',
      method: 'POST',
      tokenType: 'authToken',
    } satisfies RouteDef,
    updateWorkflowResponse: (id: string): RouteDef => ({
      url: `tags/update-workflow-response-tag/${id}`,
      method: 'PATCH',
      tokenType: 'authToken',
    }),
    removeWorkflowResponse: (id: string): RouteDef => ({
      url: `tags/delete-workflow-response-tag/${id}`,
      method: 'DELETE',
      tokenType: 'authToken',
    }),
  },
  forms: {
    list: { url: 'forms/all', method: 'POST', tokenType: 'authToken' } satisfies RouteDef,
    selection: { url: 'forms/selection', method: 'GET', tokenType: 'authToken' } satisfies RouteDef,
    create: { url: 'forms/create-form', method: 'POST', tokenType: 'authToken' } satisfies RouteDef,
    detail: (id: string): RouteDef => ({
      url: `forms/detail/${id}`,
      method: 'GET',
      tokenType: 'authToken',
    }),
    update: (id: string): RouteDef => ({
      url: `forms/update-form/${id}`,
      method: 'PATCH',
      tokenType: 'authToken',
    }),
    updateReportLayout: (id: string): RouteDef => ({
      url: `forms/update-report-layout/${id}`,
      method: 'PATCH',
      tokenType: 'authToken',
    }),
    updateApiExport: (id: string): RouteDef => ({
      url: `forms/update-api-export/${id}`,
      method: 'PATCH',
      tokenType: 'authToken',
    }),
    updateElements: {
      url: 'forms/update-elements',
      method: 'PATCH',
      tokenType: 'authToken',
    } satisfies RouteDef,
    publish: (id: string): RouteDef => ({
      url: `forms/publish/${id}`,
      method: 'PATCH',
      tokenType: 'authToken',
    }),
    bookmark: {
      url: 'forms/bookmark',
      method: 'PATCH',
      tokenType: 'authToken',
    } satisfies RouteDef,
    setTestStatus: {
      url: 'forms/set-test-status',
      method: 'PATCH',
      tokenType: 'authToken',
    } satisfies RouteDef,
    // One endpoint behind the four lifecycle verbs — body { formIds, action }
    // (the inspections bulk-action idiom; a single row and a selection are the
    // same call).
    bulkAction: {
      url: 'forms/bulk-action',
      method: 'PATCH',
      tokenType: 'authToken',
    } satisfies RouteDef,
    // TRUE bulk delete — body { formIds }. One call covers a single row and a
    // whole selection, which is what lets the row kebab and the selection bar
    // converge on one mutation and one dialog. Moves the forms to Trash
    // (restorable from there), it is not a permanent destroy.
    bulkDelete: {
      url: 'forms/bulk-delete',
      method: 'POST',
      tokenType: 'authToken',
    } satisfies RouteDef,
    updateFormsFolder: {
      url: 'forms/update-forms-folder',
      method: 'PATCH',
      tokenType: 'authToken',
    } satisfies RouteDef,
    // Creates a form from an exported design file (the Export JSON shape).
    // No `textDirection` and no element `_id`/`elementGlobalId` on purpose
    // (user-confirmed): the backend mints ids from `uniqueIdToReplace`.
    importForm: {
      url: 'forms/import-form',
      method: 'POST',
      tokenType: 'authToken',
    } satisfies RouteDef,
    duplicate: (id: string): RouteDef => ({
      url: `forms/duplicate/${id}`,
      method: 'POST',
      tokenType: 'authToken',
    }),
  },
  submissions: {
    accessed: {
      url: 'submissions/accessed-submissions',
      method: 'POST',
      tokenType: 'authToken',
    } satisfies RouteDef,
    /** The server-rendered report for one submission; `reportType` picks the
     *  `web` or `pdf` render and `language` the locale the server renders it in
     *  (`en` | `ar` — the report's own language, unrelated to the app's locale).
     *  Returns HTML, not a data payload. */
    report: (id: string, reportType: string, language: string): RouteDef => ({
      url: `submissions/report/${id}/${reportType}/${language}`,
      method: 'GET',
      tokenType: 'authToken',
    }),
    /** A presigned link to the submission's finished PDF — `{ url }`, not the
     *  file. `language` picks the render's locale exactly as `report` does; there
     *  is no `reportType` segment because this render is always the pdf one. */
    downloadPdf: (id: string, language: string): RouteDef => ({
      url: `submissions/download-pdf/${id}/${language}`,
      method: 'GET',
      tokenType: 'authToken',
    }),
    /** The submission's CURRENT workflow step — its position/total plus the
     *  response elements the reviewer can pick (the Workflow actions drawer). */
    workflowStepDetails: (id: string): RouteDef => ({
      url: `submissions/workflow-step-details/${id}`,
      method: 'GET',
      tokenType: 'authToken',
    }),
    /** Fires one of that step's response elements — the workflow transition. */
    updateStep: (submissionId: string): RouteDef => ({
      url: `submissions/update-step/${submissionId}`,
      method: 'PATCH',
      tokenType: 'authToken',
    }),
    /** Lifecycle move for one or many submissions — archive/restore/trash.
     *  Bulk-capable, so one row and a whole selection are the same call. */
    bulkAction: {
      url: 'submissions/bulk-action',
      method: 'POST',
      tokenType: 'authToken',
    } satisfies RouteDef,
    /** The submission's PDF version history — `{ history: [...] }`, newest
     *  first. A top-level resource, not under `submissions/`. */
    pdfHistory: (submissionId: string): RouteDef => ({
      url: `submission-history/details/${submissionId}`,
      method: 'GET',
      tokenType: 'authToken',
    }),
    /** A presigned link to ONE historical version's finished PDF — `{ url }`,
     *  not the file. Keyed by the history entry's id, not the submission's,
     *  and no `language` segment: a stored version is one fixed artefact. */
    pdfHistoryUrl: (historyId: string): RouteDef => ({
      url: `media/submission-history-pdf/${historyId}`,
      method: 'GET',
      tokenType: 'authToken',
    }),
    /** Starts a server-side CSV export of the selected submissions — body
     *  `{ submissionIds }`. Fire-and-forget: the response carries no payload
     *  to consume, the file lands through the export pipeline. */
    exportCsv: {
      url: 'export-manager/export-submissions',
      method: 'POST',
      tokenType: 'authToken',
    } satisfies RouteDef,
    /** Starts a server-side export of ONE submission's attachments — the id
     *  rides in the path, no body. Fire-and-forget like exportCsv. */
    exportAttachments: (submissionId: string): RouteDef => ({
      url: `export-manager/export-submission-attachments/${submissionId}`,
      method: 'POST',
      tokenType: 'authToken',
    }),
  },
  formChoices: {
    all: { url: 'form-choices/all', method: 'GET', tokenType: 'authToken' } satisfies RouteDef,
    create: {
      url: 'form-choices/create',
      method: 'POST',
      tokenType: 'authToken',
    } satisfies RouteDef,
    detail: (id: string): RouteDef => ({
      url: `form-choices/${id}`,
      method: 'GET',
      tokenType: 'authToken',
    }),
    update: (id: string): RouteDef => ({
      url: `form-choices/update/${id}`,
      method: 'PATCH',
      tokenType: 'authToken',
    }),
    /** Deletes one or many in a single call — body `{ formChoiceIds }`. There is
     *  no per-id DELETE; a one-row delete just sends a one-element array. */
    bulkDelete: {
      url: 'form-choices/bulk-delete',
      method: 'POST',
      tokenType: 'authToken',
    } satisfies RouteDef,
  },
  formFolders: {
    list: { url: 'form-folders', method: 'GET', tokenType: 'authToken' } satisfies RouteDef,
    create: { url: 'form-folders', method: 'POST', tokenType: 'authToken' } satisfies RouteDef,
    update: (id: string): RouteDef => ({
      url: `form-folders/${id}`,
      method: 'PATCH',
      tokenType: 'authToken',
    }),
    remove: (id: string): RouteDef => ({
      url: `form-folders/${id}`,
      method: 'DELETE',
      tokenType: 'authToken',
    }),
  },
  workflows: {
    list: { url: 'workflows/all', method: 'POST', tokenType: 'authToken' } satisfies RouteDef,
    selection: {
      url: 'workflows/selection',
      method: 'GET',
      tokenType: 'authToken',
    } satisfies RouteDef,
    draft: { url: 'workflows/draft', method: 'POST', tokenType: 'authToken' } satisfies RouteDef,
    publish: {
      url: 'workflows/publish',
      method: 'POST',
      tokenType: 'authToken',
    } satisfies RouteDef,
    detail: (id: string): RouteDef => ({
      url: `workflows/${id}/details`,
      method: 'GET',
      tokenType: 'authToken',
    }),
    versions: (id: string): RouteDef => ({
      url: `workflows/${id}/versions`,
      method: 'GET',
      tokenType: 'authToken',
    }),
    status: (id: string): RouteDef => ({
      url: `workflows/${id}/status`,
      method: 'PATCH',
      tokenType: 'authToken',
    }),
    duplicate: (id: string): RouteDef => ({
      url: `workflows/${id}/duplicate`,
      method: 'POST',
      tokenType: 'authToken',
    }),
  },
  inspections: {
    list: { url: 'inspections/all', method: 'POST', tokenType: 'authToken' } satisfies RouteDef,
    accessibleUsers: {
      url: 'inspections/accessible-inspection-users',
      method: 'GET',
      tokenType: 'authToken',
    } satisfies RouteDef,
    create: {
      url: 'inspections/create',
      method: 'POST',
      tokenType: 'authToken',
    } satisfies RouteDef,
    saveDraft: {
      url: 'inspections/save-draft',
      method: 'POST',
      tokenType: 'authToken',
    } satisfies RouteDef,
    update: (id: string): RouteDef => ({
      url: `inspections/update/${id}`,
      method: 'PATCH',
      tokenType: 'authToken',
    }),
    // Full single-inspection payload (adds companyLevel/companyNode/location/
    // coordinates over the list rows) — feeds the edit drawer.
    detail: (id: string): RouteDef => ({
      url: `inspections/details/${id}`,
      method: 'GET',
      tokenType: 'authToken',
    }),
    // Users auto-assigned by the inspection's scope (project + level + nodes).
    scopeUsers: {
      url: 'inspections/scope-users',
      method: 'POST',
      tokenType: 'authToken',
    } satisfies RouteDef,
    // One endpoint behind four lifecycle verbs — body { inspectionIds, action }.
    // Duplicate/suspend/resume act on a single inspection, archive on many;
    // the id array is what makes both shapes the same call.
    bulkAction: {
      url: 'inspections/bulk-action',
      method: 'POST',
      tokenType: 'authToken',
    } satisfies RouteDef,
    // TRUE bulk delete — body { inspectionIds }. One call covers a single row
    // and a whole selection, which is what lets the row kebab and the selection
    // bar converge on one mutation and one dialog (the data-source idiom).
    // There is no single-delete route any more; this replaced it.
    bulkDelete: {
      url: 'inspections/bulk-delete',
      method: 'POST',
      tokenType: 'authToken',
    } satisfies RouteDef,
  },
  companyFields: {
    // The shared vocabulary a form's companyField element picks from; each one
    // reads its allowed values from a data source file + column.
    create: { url: 'company-field', method: 'POST', tokenType: 'authToken' } satisfies RouteDef,
    // Paged list — body { page, limit } plus the optional search and toolbar
    // filters (status, dataSource, lastUpdated + a custom start/end range).
    list: { url: 'company-field/all', method: 'POST', tokenType: 'authToken' } satisfies RouteDef,
    // Picker feed for the form builder's companyField element (id + title + the
    // data source/column the field reads from).
    selection: {
      url: 'company-field/selection',
      method: 'GET',
      tokenType: 'authToken',
    } satisfies RouteDef,
    // Edit — create's body minus dbColumn (a column name can't change).
    update: (id: string): RouteDef => ({
      url: `company-field/${id}`,
      method: 'PATCH',
      tokenType: 'authToken',
    }),
    duplicate: (id: string): RouteDef => ({
      url: `company-field/duplicate/${id}`,
      method: 'POST',
      tokenType: 'authToken',
    }),
    // Hide/show — body { isHidden }.
    status: (id: string): RouteDef => ({
      url: `company-field/status/${id}`,
      method: 'PATCH',
      tokenType: 'authToken',
    }),
  },
  formSets: {
    // Option sets (wire name "form sets") — the Data structure tab's reusable
    // picklists, each a titled list of options with a SQL column. `type` says
    // whether the set attaches at form or question level.
    create: { url: 'form-sets/create', method: 'POST', tokenType: 'authToken' } satisfies RouteDef,
    // Paged list — body { page, limit } plus the optional search.
    list: { url: 'form-sets/list', method: 'POST', tokenType: 'authToken' } satisfies RouteDef,
    // The Data setup module's "Manage sets" catalogue — every set the company
    // has, with its options, so a set can become a table column. Both answer a
    // BARE ARRAY (no envelope), the shape every other /selection feed here uses.
    // Which one you want is decided by where the set attaches:
    //   · form level     → the "Form meta & SQL" tab
    //   · question level → the "Form structure" tab
    selectionFormSets: {
      url: 'form-sets/selection-form-sets',
      method: 'GET',
      tokenType: 'authToken',
    } satisfies RouteDef,
    selectionQuestionSets: {
      url: 'form-sets/selection-question-sets',
      method: 'GET',
      tokenType: 'authToken',
    } satisfies RouteDef,
    // Edit — title + options only ({ title, options, deletedOptions }): neither
    // `type` nor `sqlColumn` is in the body, so both are fixed once created.
    update: (id: string): RouteDef => ({
      url: `form-sets/update/${id}`,
      method: 'PATCH',
      tokenType: 'authToken',
    }),
    /** Deletes one or many in a single call — body `{ formSetIds }`. There is
     *  no per-id DELETE; a one-row delete just sends a one-element array. */
    bulkDelete: {
      url: 'form-sets/bulk-delete',
      method: 'POST',
      tokenType: 'authToken',
    } satisfies RouteDef,
  },
  topicSets: {
    // Topics (wire name "topic sets") — the Data structure tab's subject labels
    // that group forms and inspections so reports can roll up by subject. A
    // topic is just a title; create and update share the `{ title }` body.
    create: { url: 'topic-sets/create', method: 'POST', tokenType: 'authToken' } satisfies RouteDef,
    // Paged list — body { page, limit } plus the optional search.
    list: { url: 'topic-sets/all', method: 'POST', tokenType: 'authToken' } satisfies RouteDef,
    // Bare `[{ _id, title }]` catalogue — the Data setup module's Form structure
    // tab resolves a row's topic TITLE back to an id through it, since the save
    // sends ids. Mirrors the form-sets selection feeds.
    selection: {
      url: 'topic-sets/selection',
      method: 'GET',
      tokenType: 'authToken',
    } satisfies RouteDef,
    update: (id: string): RouteDef => ({
      url: `topic-sets/update/${id}`,
      method: 'PATCH',
      tokenType: 'authToken',
    }),
    /** Deletes one or many in a single call — body `{ topicSetIds }`. There is
     *  no per-id DELETE; a one-row delete just sends a one-element array. */
    bulkDelete: {
      url: 'topic-sets/bulk-delete',
      method: 'POST',
      tokenType: 'authToken',
    } satisfies RouteDef,
  },
  dataSetup: {
    // Rows for the Data setup module's "Form meta & SQL" tab: every form
    // template plus how it lands in SQL. Body is `{ page, limit, search? }` —
    // still no orderBy, which is why the page ships without sort glyphs.
    listForms: {
      url: 'dashboard-setup/list-forms',
      method: 'POST',
      tokenType: 'authToken',
    } satisfies RouteDef,
    // Rows for the "Form structure" tab: one row per FIELD across every form
    // template. Body is `{ page, limit }` and nothing else — no search, no
    // orderBy, no filter keys — which is why that tab ships without a search
    // box, a Filter control or a single sort glyph.
    //
    // NOTE the envelope: this one answers `{ records, totalCount }`, NOT the
    // `{ items, totalRecords }` that listForms above returns — and exports/list
    // answers `{ items }`. Three shapes in one API; they genuinely disagree, so
    // this is not a typo to tidy up. (Reading `rows` here, the legacy name, is
    // what once left the tab empty under a correct row count.)
    fetchStructure: {
      url: 'dashboard-setup/fetch-structure',
      method: 'POST',
      tokenType: 'authToken',
    } satisfies RouteDef,
    // Saves the "Manage sets" work on the Form meta & SQL tab in one call:
    // `{ metadata, addedFormSetIds, removedFormSetIds }`, where `metadata`
    // carries only the rows whose pick CHANGED and a null `formSetOptionId`
    // clears an assignment. The two id lists are a delta against what was last
    // saved, not the full selection.
    updateMetadata: {
      url: 'dashboard-setup/update-metadata',
      method: 'POST',
      tokenType: 'authToken',
    } satisfies RouteDef,
    // Connect a form — body `{ tableName, formId }`. This IS the save: the
    // table name has no endpoint of its own, so it is persisted only by
    // connecting. A duplicate name is rejected HERE (there is no pre-check
    // call), so the caller surfaces the response's own message.
    addVerticalExport: {
      url: 'exports/add-vertical-export',
      method: 'POST',
      tokenType: 'authToken',
    } satisfies RouteDef,
    // Delete ANY export — a form's (DataSetupForm.exportId) or a company-wide
    // one (CompanyExport.id). One route serves both; it addresses the export
    // entry by its own id, never the form's.
    removeExport: (id: string): RouteDef => ({
      url: `exports/${id}/export`,
      method: 'DELETE',
      tokenType: 'authToken',
    }),
    // Every export the company holds, `{ items }`-wrapped. The SQL settings
    // drawer filters it down to its four types client-side, so it works whether
    // this returns only those or the per-form and form-structure ones too.
    exportsList: {
      url: 'exports/list',
      method: 'GET',
      tokenType: 'authToken',
    } satisfies RouteDef,
    // Create a company-wide export — body `{ tableName, type }`, no formId.
    // Accepts the four drawer types PLUS form_structure_export.
    addCompanyExport: {
      url: 'exports/add-company-exports',
      method: 'POST',
      tokenType: 'authToken',
    } satisfies RouteDef,
    // Re-run a company-wide export. Keyed by TYPE, not by the export's id —
    // body `{ type }`.
    reimportCompanyExport: {
      url: 'exports/reimport-company-export',
      method: 'POST',
      tokenType: 'authToken',
    } satisfies RouteDef,
    // Re-run one form's vertical export. Keyed by FORM — body `{ formId }`.
    // Its own route: the company one can't express a per-form target.
    reimportFormVerticalExport: {
      url: 'exports/reimport-form-vertical-export',
      method: 'POST',
      tokenType: 'authToken',
    } satisfies RouteDef,
    // Forms whose vertical export is live — the Power BI modal's candidates.
    // Takes no params and returns the whole list, which is why that modal
    // filters in memory rather than round-tripping a search term.
    enabledVerticalExportForms: {
      url: 'exports/enabled-vertical-export-forms',
      method: 'GET',
      tokenType: 'authToken',
    } satisfies RouteDef,
    // Marks which forms have a Power BI view. REPLACES the whole set — the body
    // carries every marked form, not a delta — which is why the modal drafts
    // locally and commits once on Done.
    addPowerBiExport: {
      url: 'exports/add-powerbi-export',
      method: 'POST',
      tokenType: 'authToken',
    } satisfies RouteDef,
  },
  dataSource: {
    list: { url: 'datasource/all', method: 'POST', tokenType: 'authToken' } satisfies RouteDef,
    // Lightweight picker feed (id + name + column schema) for the builder's
    // dataSource field.
    selection: {
      url: 'datasource/selection',
      method: 'GET',
      tokenType: 'authToken',
    } satisfies RouteDef,
    create: {
      url: 'datasource',
      method: 'POST',
      tokenType: 'authToken',
    } satisfies RouteDef,
    // Takes the CREATE body unchanged, so one modal serves both modes.
    update: (id: string): RouteDef => ({
      url: `datasource/${id}`,
      method: 'PATCH',
      tokenType: 'authToken',
    }),
    details: (id: string): RouteDef => ({
      url: `datasource/${id}/details`,
      method: 'GET',
      tokenType: 'authToken',
    }),
    deleteMultiple: {
      url: 'datasource/delete-multiple',
      method: 'POST',
      tokenType: 'authToken',
    } satisfies RouteDef,
    addColumn: (id: string): RouteDef => ({
      url: `datasource/${id}/columns`,
      method: 'POST',
      tokenType: 'authToken',
    }),
    // Renames a column — the body is `{ columnName }` ONLY, so a column's
    // dataType cannot be changed once it exists.
    updateColumn: (id: string, columnId: string): RouteDef => ({
      url: `datasource/${id}/columns/${columnId}`,
      method: 'PATCH',
      tokenType: 'authToken',
    }),
    deleteColumns: (id: string): RouteDef => ({
      url: `datasource/${id}/columns/delete-multiple`,
      method: 'POST',
      tokenType: 'authToken',
    }),
  },
  dataSourceEntries: {
    list: {
      url: 'data-source-entries/all',
      method: 'POST',
      tokenType: 'authToken',
    } satisfies RouteDef,
    create: {
      url: 'data-source-entries',
      method: 'POST',
      tokenType: 'authToken',
    } satisfies RouteDef,
    update: (id: string): RouteDef => ({
      url: `data-source-entries/${id}`,
      method: 'PATCH',
      tokenType: 'authToken',
    }),
    deleteMultiple: {
      url: 'data-source-entries/delete-multiple',
      method: 'POST',
      tokenType: 'authToken',
    } satisfies RouteDef,
  },
  // Named `imports`, not `import` — the keyword reads badly at the call site.
  imports: {
    /** Creates a data source FROM an uploaded CSV: the file is already in the
     *  bucket, so the body references it by name + url rather than carrying it. */
    dataSource: {
      url: 'import/import-datasource',
      method: 'POST',
      tokenType: 'authToken',
    } satisfies RouteDef,
  },
  activityLogs: {
    list: { url: 'activity-logs/list', method: 'POST', tokenType: 'authToken' } satisfies RouteDef,
  },
  trash: {
    // Soft-deleted entities awaiting restore or automatic purge. Server-side
    // pagination + the deletedBy/whenDeleted(fromDate,toDate)/purged filters.
    list: { url: 'trash/list', method: 'POST', tokenType: 'authToken' } satisfies RouteDef,
    // Restore the given rows to their modules — body { ids }. Bulk-capable:
    // one row and a selection are the same call. Gated by the Trash module's
    // `restore` permission (bulk-delete has its own).
    bulkRestore: {
      url: 'trash/bulk-restore',
      method: 'POST',
      tokenType: 'authToken',
    } satisfies RouteDef,
    // Permanently delete the given rows — body { ids }. Same bulk shape,
    // gated by the Trash module's `delete` permission.
    bulkDelete: {
      url: 'trash/bulk-delete',
      method: 'POST',
      tokenType: 'authToken',
    } satisfies RouteDef,
  },
  attendance: {
    list: {
      url: 'attendance/attendance-list',
      method: 'POST',
      tokenType: 'authToken',
    } satisfies RouteDef,
    // Today's headline counts for the summary tiles; also carries the
    // checked-in users feeding the live-locations list + map container.
    todayStats: {
      url: 'attendance/today-stats',
      method: 'GET',
      tokenType: 'authToken',
    } satisfies RouteDef,
  },
  // Company API keys (attendance module grant). The key itself is only ever in
  // the create response; revoke needs active keys, delete needs revoked ones.
  apiKeys: {
    create: { url: 'api-keys', method: 'POST', tokenType: 'authToken' } satisfies RouteDef,
    list: { url: 'api-keys/list', method: 'POST', tokenType: 'authToken' } satisfies RouteDef,
    bulkRevoke: {
      url: 'api-keys/bulk-revoke',
      method: 'POST',
      tokenType: 'authToken',
    } satisfies RouteDef,
    bulkDelete: {
      url: 'api-keys/bulk-delete',
      method: 'POST',
      tokenType: 'authToken',
    } satisfies RouteDef,
  },
  liveTracking: {
    // The manager's trackable users (their structure subtree ∩ their
    // projects), each with attendance, last location and trail. Live updates
    // then arrive over the /live-tracking socket (api/liveTrackingSocket.ts).
    linkedUsers: {
      url: 'live-tracking/linked-users',
      method: 'GET',
      tokenType: 'authToken',
    } satisfies RouteDef,
    // One linked user's route history: every clock in the range with its
    // points ({ userId, dateFilter, startDate?/endDate? for 'custom' }).
    linkedUserRoutes: {
      url: 'live-tracking/linked-user-routes',
      method: 'POST',
      tokenType: 'authToken',
    } satisfies RouteDef,
  },
  // Company-wide general settings; only the attendance group is wired so far.
  generalSettings: {
    // _id: null in the response = settings never created (first save POSTs add).
    fetchAttendanceSettings: {
      url: 'general-settings/fetch-attendance-settings',
      method: 'GET',
      tokenType: 'authToken',
    } satisfies RouteDef,
    addAttendanceSetting: {
      url: 'general-settings/add-attendance-settings',
      method: 'POST',
      tokenType: 'authToken',
    } satisfies RouteDef,
    updateAttendanceSettings: (id: string): RouteDef => ({
      url: `general-settings/update-attendance-settings/${id}`,
      method: 'PATCH',
      tokenType: 'authToken',
    }),
  },
  notifications: {
    list: {
      url: 'notifications-history/my-notifications',
      method: 'POST',
      tokenType: 'authToken',
    } satisfies RouteDef,
    readAll: {
      url: 'notifications-history/read-all',
      method: 'PATCH',
      tokenType: 'authToken',
    } satisfies RouteDef,
    readOne: (id: string): RouteDef => ({
      url: `notifications-history/${id}/read`,
      method: 'PATCH',
      tokenType: 'authToken',
    }),
  },
  exportManager: {
    exportGuest: {
      url: 'export-manager/export-guests',
      method: 'POST',
      tokenType: 'authToken',
    } satisfies RouteDef,
    // Queues a people export ({ status: 'active' | 'archived' } picks the tab's
    // population). Fire-and-forget: the file lands in the export manager.
    exportPeople: {
      url: 'export-manager/export-people',
      method: 'POST',
      tokenType: 'authToken',
    } satisfies RouteDef,
    // Queues an export of one data source's entries. Fire-and-forget like its
    // sibling: the response says the job started, not where the file is.
    exportDataSource: {
      url: 'export-manager/export-datasource',
      method: 'POST',
      tokenType: 'authToken',
    } satisfies RouteDef,
    // Queues an export of form templates — body `{ formIds }` ([] = the whole
    // listing). Fire-and-forget: the file lands through the export pipeline.
    exportForms: {
      url: 'export-manager/export-forms',
      method: 'POST',
      tokenType: 'authToken',
    } satisfies RouteDef,
    // Queues a CSV export of one form's design — body `{ formId }`. Fire-and-
    // forget like its siblings: the file lands through the export pipeline.
    exportFormDesign: {
      url: 'export-manager/form-design-export',
      method: 'POST',
      tokenType: 'authToken',
    } satisfies RouteDef,
    // Where the file is: resolves a finished job (the id arrives on the
    // export notification's details) to its file URL — `{ downloadUrl }`.
    downloadUrl: (exportId: string): RouteDef => ({
      url: `export-manager/${exportId}/download-url`,
      method: 'GET',
      tokenType: 'authToken',
    }),
  },
  sharing: {
    // Resolves a shared bug-report id to its destination URL — `{ url }`, not
    // the report itself. loginToken (pre-company session): shared links open
    // before any company is selected.
    bugReport: (id: string): RouteDef => ({
      url: `sharing/bug-report/${id}`,
      method: 'GET',
      tokenType: 'loginToken',
    }),
  },
};
